import { Trans, useTranslation } from "react-i18next";

import { ALL_SCHEMAS, bulkItemTitleMap } from "../PrivilegesTable/constant";
import {
  ROLES_WIZARD_ADD_GROUP_EMPTY,
  ROLES_WIZARD_IGNORE_ERROR,
  ROLES_WIZARD_SET_ASSIGN_PRIVILEGES,
  ROLES_WIZARD_SET_RESOURCE_OBJECTS,
} from "../privilegesReducer";
import { PermissionError, PrivilegesState } from "../types";
import { usePrivilegesState } from "../usePrivilegesContext";
import { formatPrivilege } from "../utils";
import { MissingPermissionError } from "./MissingPermissionError";

type Props = {
  error: PermissionError;
  resource: string;
  catalogName?: string;
};

export const MissingSchemaPermission = (props: Props) => {
  const { t } = useTranslation();
  const { error, resource, catalogName } = props;
  const { privilegesState, privilegesDispatch } = usePrivilegesState();

  const addRelatedPermissions = () => {
    const { resource, action, object } = error;
    const resourceGroups = catalogName
      ? privilegesState.databasePrivileges[catalogName][
          resource as keyof PrivilegesState
        ]
      : privilegesState[resource as keyof PrivilegesState];

    const availableGroupByObjects = resourceGroups.findIndex(group => {
      return (
        group.objects.length === 1 &&
        typeof group.objects[0] !== "string" &&
        group.objects[0].name === object &&
        (group.objects[0].catalogName === error.catalogName ||
          group.objects[0].catalogName === error.errorSource.catalogName ||
          group.objects[0].catalogName === catalogName)
      );
    });

    const availableGroupByActions = resourceGroups.findIndex(group => {
      return group.toAssign.length === 1 && group.toAssign[0] === action;
    });

    const availableGroupByEmptyPrivileges = resourceGroups.findIndex(group => {
      return (
        group.toAssign.length === 0 &&
        (group.objects.length === 0 ||
          (typeof group.objects?.[0] !== "string" &&
            group.objects[0].name === ALL_SCHEMAS))
      );
    });

    if (availableGroupByEmptyPrivileges !== -1) {
      privilegesDispatch({
        type: ROLES_WIZARD_SET_RESOURCE_OBJECTS,
        resource: "schema",
        objects: [
          {
            name: object,
            catalogName: catalogName || error.errorSource.catalogName,
          },
        ],
        index: availableGroupByEmptyPrivileges,
        catalogName,
      });
      privilegesDispatch({
        type: ROLES_WIZARD_SET_ASSIGN_PRIVILEGES,
        resource: "schema",
        privileges: [action],
        index: availableGroupByEmptyPrivileges,
        catalogName,
      });
    } else if (availableGroupByObjects !== -1) {
      const group = resourceGroups[availableGroupByObjects];
      privilegesDispatch({
        type: ROLES_WIZARD_SET_ASSIGN_PRIVILEGES,
        resource: "schema",
        privileges: [...group.toAssign, action],
        index: availableGroupByObjects,
        catalogName,
      });
    } else if (availableGroupByActions !== -1) {
      const group = resourceGroups[availableGroupByActions];
      privilegesDispatch({
        type: ROLES_WIZARD_SET_RESOURCE_OBJECTS,
        resource: "schema",
        objects: [
          ...group.objects,
          {
            name: object,
            catalogName: catalogName || error.errorSource.catalogName,
          },
        ],
        index: availableGroupByActions,
        catalogName,
      });
    } else {
      privilegesDispatch({
        type: ROLES_WIZARD_ADD_GROUP_EMPTY,
        resource: "schema",
        objects: [
          {
            name: object,
            catalogName: catalogName || error.errorSource.catalogName,
          },
        ],
        catalogName,
      });

      privilegesDispatch({
        type: ROLES_WIZARD_SET_ASSIGN_PRIVILEGES,
        resource: "schema",
        privileges: [action],
        index: resourceGroups.length,
        catalogName,
      });
    }
  };

  const ignorePermissionError = () => {
    privilegesDispatch({
      type: ROLES_WIZARD_IGNORE_ERROR,
      resource,
      error,
    });
  };

  const { errorSource } = error;
  const { name, actions } = errorSource;
  const isBulk = bulkItemTitleMap[name as keyof typeof bulkItemTitleMap];
  const resourceName = isBulk
    ? t(bulkItemTitleMap[name as keyof typeof bulkItemTitleMap])
    : name;
  return (
    <MissingPermissionError
      addRelatedPermissions={addRelatedPermissions}
      ignorePermissionError={ignorePermissionError}
      primaryActionText={t("roles_wizard.missing_permission.add")}
      message={
        <Trans
          i18nKey="roles_wizard.missing_permission.message_schema"
          components={{ bold: <strong /> }}
        />
      }
      messagePermission={
        <Trans
          i18nKey="roles_wizard.missing_permission.message_permission"
          values={{
            permission: actions.map(formatPrivilege).join(", "),
            resource: resourceName,
            resourceType: isBulk ? "" : resource,
          }}
          components={{ bold: <strong /> }}
        />
      }
    />
  );
};
